import React from 'react';
import ReactDOM from 'react-dom/client';
import { datadogRum } from '@datadog/browser-rum';

import App from '@/App';
import '@/styles/global.css';

// Initialise Datadog Browser RUM — only when both credentials are provided.
// Leave VITE_DD_CLIENT_TOKEN and VITE_DD_APPLICATION_ID empty in local dev;
// they are injected from AWS Secrets Manager in production ECS builds.
const ddClientToken = import.meta.env.VITE_DD_CLIENT_TOKEN as string | undefined;
const ddApplicationId = import.meta.env.VITE_DD_APPLICATION_ID as string | undefined;

if (ddClientToken && ddApplicationId) {
  datadogRum.init({
    applicationId: ddApplicationId,
    clientToken: ddClientToken,
    site: 'datadoghq.eu',
    service: 'securegate-web',
    env: (import.meta.env.VITE_APP_ENV as string | undefined) ?? 'development',
    version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '1.0.0',

    // Collect all sessions but no session replay — replay captures screen content
    // which is a PII risk under KDPA. Enable only after a dedicated PIA.
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,

    // Track interactions, resources, and long tasks for Core Web Vitals.
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,

    // Mask all form inputs at the SDK level as a defence-in-depth measure.
    // All PII (phone, email, national ID) entered by users must never appear
    // in RUM event payloads. See docs/02-architecture/security-model.md §PII.
    defaultPrivacyLevel: 'mask-user-input',
  });
}

const root = document.getElementById('root');

if (!root) {
  throw new Error('Root element not found. Ensure index.html has <div id="root">');
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the service worker to make the web app installable (PWA).
// Production only — registering in dev interferes with Vite HMR and would
// serve stale module graphs. The SW itself is network-first for navigations,
// so deploys are picked up without a hard refresh.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Swallow registration errors — the app works fine without the SW,
      // it just won't be installable on this visit.
    });
  });
}
